import {IFilterControl} from "../models/report-interface";
import {DropDownRefDataFilter} from "../models/filterControls/drop-down-ref-data-filter-model";
import {BreachListData, BreachData, BreachListFilters} from "../models/breach-list-model";
import {BreachType, LicenceType} from "../models/reference-data-model";
import {SortControlComponent} from "gaming-component-ngx-common";
import {BreachListDataService} from "../services/breaches-list-data-service";
import { Component, OnInit } from "@angular/core";
import * as momentTz from "moment-timezone";
import {ActivatedRoute, Router} from "@angular/router";
import {isNullOrUndefined} from "util";
import { CacheService } from "../services/cache.service";
import {InputFilterModel} from "../models/filterControls/input-filter-model";

@Component({
  providers: [BreachListDataService],
  selector: "breachesList",
  templateUrl: "./breaches-list.component.html",
})
export class BreachesListComponent implements OnInit {

  public filterControls: IFilterControl[] = new Array<IFilterControl>();
  public filterText = "Filter Breaches";
  public data: BreachListData = null;
  public reportTitle: string = "Breaches Summary";
  public searching: any = {columnNames: ["licenceId", "licenceName"], searchString: ""};
  public errorState: string;
  public isLoading: boolean = true;
  public doRefreshSearch: boolean = false;

  private breachListDataService: BreachListDataService;
  // Filter info for CSV header
  private filterBtype: string;
  private filterLicenceType: string;
  private filterTolerance: number;
  private filterSearchString: string;
  private venueId: string;
  private dateFrom: string = null;
  private dateTo: string = null;
  private today = new Date();

  constructor(rds: BreachListDataService, private route: ActivatedRoute, private router: Router, private cacheService: CacheService) {
    this.breachListDataService = rds;
  }

  public isFiltered(): boolean {
    return !isNullOrUndefined(this.filterBtype) || !isNullOrUndefined(this.filterLicenceType);
  }

  public async ngOnInit() {
    // Init filter dropdown
    const btype = new DropDownRefDataFilter("BreachType", "Breach Type", BreachType.GetAll(), "Breaches");
    btype.sizeClass = "five";
    const licenceType = new DropDownRefDataFilter("LicenceType", "Licence Type", LicenceType.GetAll(), "Breaches");
    licenceType.sizeClass = "five";
    const tolerance = new InputFilterModel("Tolerance", "Tolerance($)", "", "", "NumberFilter", "two");
    this.filterControls = [btype, licenceType, tolerance];

    const config = await this.breachListDataService.getBreachConfig();

    this.route.paramMap.subscribe((paramsMap) => {
      this.venueId = decodeURI(paramsMap.get("venueId"));
      if (this.venueId === "undefined" || this.venueId === "null") { this.venueId = undefined; }
      if (this.cacheService.isVenueIdChanged(this.venueId)) {
        this.cacheService.clearCache();
      }
      const cachedFilters = this.cacheService.getBreachListFilters();
      this.filterTolerance = 100;
      cachedFilters.tolerance = 100;
      this.filterControls[2].activate(100);
      if (config.defaultLicenceTypeFilter) {
        this.filterLicenceType = config.defaultLicenceTypeFilter;
        if (LicenceType[config.defaultLicenceTypeFilter]) {
          this.filterControls[1].activate(LicenceType[config.defaultLicenceTypeFilter]);
        }
        cachedFilters.licenceType = config.defaultLicenceTypeFilter;

      }

      this.cacheService.setBreachListFilters(cachedFilters);

      this.initBreaches();

    });

  }

  public initBreaches() {
    const queryObj: any = { // Default date range is 3 days ago until now
      dateFrom: this.cvtz().subtract(3, "days").startOf("day").valueOf(),
      dateTo: this.cvtz().valueOf(),
    };

    // cacheService
    const cachedDateRange = this.cacheService.getBreachListFilters().dateRange;
    if (cachedDateRange) {
      const arrDateRange = cachedDateRange.split(" to ");
      if (arrDateRange.length === 2) {
        queryObj.dateFrom = this.cvtz(new Date(arrDateRange[0]).getTime()).startOf("day").valueOf();
        queryObj.dateTo = this.cvtz(new Date(arrDateRange[1]).getTime()).endOf("day").valueOf();
        this.dateFrom = arrDateRange[0];
        this.dateTo =  arrDateRange[1];
      }
    }

    if (!isNullOrUndefined(this.venueId)) {
      queryObj.dateFrom = "0";
      queryObj.dateTo = "";
    }

    // cacheService
    const cachedBreachListData = this.cacheService.getBreachListData();
    if (cachedBreachListData) {
      this.data = cachedBreachListData;
      const cachedFilters = this.refreshFiltersFromCache();
      this.data.filters = cachedFilters; // Make sure filter info for CSV header is up to date
      this.isLoading = false;
    } else {
      this.setDateRange(queryObj);
    }
  }

  public onFilterApply(filterdata: IFilterControl[]) {
    let result = new Array<BreachData>();
    const allRowdata: BreachData[] = this.data.tableData.allRows;
    const btype: string = filterdata[0].selectedData.value;
    const licenceType: string = filterdata[1].selectedData.value;
    const breachTolerance: number = filterdata[2].selectedData;

    // Filter info for CSV header
    this.filterBtype = btype; // Needed because setDateRange() resets data.filters
    this.filterLicenceType = licenceType;
    this.filterTolerance = breachTolerance;
    this.data.filters.btype = this.filterBtype;
    this.data.filters.licenceType = this.filterLicenceType;
    this.data.filters.tolerance = this.filterTolerance;
    this.cacheService.setBreachListFilters(this.data.filters); // cacheService

    if ( !(btype || licenceType || breachTolerance) ) {
      result = allRowdata;
      this.filterText = "Filter Breaches";
    } else {
      result = allRowdata.filter((row) => {
        if (btype) {
          if (row.breachType === "Shutdown and Trading Hours" &&
            (btype !== "Trading Hours" && btype !== "Shutdown Hours")) {
              return false;
          }
          if (!row.breachType || row.breachType !== btype) {
            return false;
          }
        }

        if (licenceType) {
          if (!row.licenceType || row.licenceType.toLowerCase() !== licenceType.toLowerCase()) {
            return false;
          }
        }

        if (breachTolerance) {
          if (!row.turnOver || row.turnOver < breachTolerance) {
            return false;
          }
        }

        return true;
      });
      const filterCount = [ btype, licenceType, breachTolerance ].reduce((count: number, filter) => {
        if (filter) {
          return ++count;
        }
        return count;
      } , 0);

      this.filterText = `${filterCount} Filter(s) Applied`;
    }
    this.data.tableData.filteredRows = result.slice();
    this.data.tableData.rows = result.slice(); // clone array

    // Trigger manual TableSearchComponent refresh
    this.doRefreshSearch = true;
    setTimeout(() => {
      this.doRefreshSearch = false;
    }, 500); // Reset doRefreshSearch back to false
  }

  public setDateRange(queryObj) {
    // Reset this.data on each date range change and show loading screen
    this.data = null;
    this.isLoading = true;

    if (!isNullOrUndefined(this.venueId)) { queryObj.venueId = this.venueId; }
    if (queryObj.dateFrom > 0) { this.dateFrom = this.cvtz(queryObj.dateFrom).format("DD MMM YYYY"); } else { this.dateFrom = null; }
    if (queryObj.dateTo !== '') { this.dateTo = this.cvtz(queryObj.dateTo).format("DD MMM YYYY"); } else { this.dateTo = null; }

    this.breachListDataService.getBreachData(queryObj).then(
      (rslt) => {
        let refreshFilter = false;
        this.data = rslt;

        // cacheService
        const cachedFilters = this.refreshFiltersFromCache();
        if (cachedFilters.btype || cachedFilters.licenceType) {
          refreshFilter = true;
        }

        // Filter info for CSV header
        if (this.dateFrom && this.dateTo) {
          this.data.filters.dateRange = `${this.dateFrom} to ${this.dateTo}`;
        } else {
          this.data.filters.dateRange = undefined;
        }
        this.data.filters.venueId = this.venueId;
        // Needed because setDateRange() resets data.filters
        this.data.filters.btype = this.filterBtype;
        this.data.filters.licenceType = this.filterLicenceType;
        this.data.filters.searchString = this.filterSearchString;
        this.cacheService.setBreachListFilters(this.data.filters); // cacheService

        SortControlComponent.ChangeTableSort(this.data.tableData);

        if (refreshFilter) {
          refreshFilter = false;
          this.onFilterApply(this.filterControls);
        }

        this.cacheService.setBreachListData(this.data); // cacheService

        this.isLoading = false;
      },
    )
    .catch((error) => {
      this.errorState = JSON.stringify(error);
      this.isLoading = false; // Manually set isLoading=false because we moved dimmer logic outside so we can reuse it
    });
  }

  // Filter info for CSV header
  public outSearchString(searchString): void {
    this.filterSearchString = searchString; // Needed because setDateRange() resets data.filters
    if (this.data) {
      this.data.filters.searchString = this.filterSearchString;
      this.cacheService.setBreachListFilters(this.data.filters); // cacheService
    }
  }

  // cacheService
  private refreshFiltersFromCache(): BreachListFilters {
    const cachedFilters = this.cacheService.getBreachListFilters();
    if (cachedFilters) {
      if (cachedFilters.searchString) {
        this.filterSearchString = cachedFilters.searchString;
        this.searching.searchString = cachedFilters.searchString;
      }
      if (cachedFilters.btype) {
        this.filterBtype = cachedFilters.btype;
        const selectedBreachType = BreachType.GetAll().filter((breachType) => {
          return breachType.value === cachedFilters.btype;
        })[0];
        if (selectedBreachType) {
          this.filterControls[0].activate(selectedBreachType);
        }
      }
      if (cachedFilters.licenceType) {
        this.filterLicenceType = cachedFilters.licenceType;
        const selectedLicenceType = LicenceType.GetAll().filter((licenceType) => {
          return licenceType.value === cachedFilters.licenceType;
        })[0];
        if (selectedLicenceType) {
          this.filterControls[1].activate(selectedLicenceType);
        }
      }
      if (cachedFilters.tolerance) {
        this.filterTolerance = cachedFilters.tolerance;
        this.filterControls[2].activate(cachedFilters.tolerance);
      }
    }
    return cachedFilters;
  }

  private cvtz(date?: any): any {
    return momentTz(date).tz('Australia/Sydney');
  }
}



// WEBPACK FOOTER //
// ./client/app/breaches/breaches-list.component.ts